import type { CartItem, Product } from './types'
import { getBuyerCommerceContext } from './commerce-context'

export type PricingAudience =
  | 'guest'
  | 'cpf'
  | 'cnpj_with_ie'
  | 'cnpj_without_ie'
  | 'unknown'

export type PricingHintKind = 'distribution' | 'cnpj_ie' | 'cnpj_ie_no_difal' | null

export type PricingDistributionInfo = {
  hasData: boolean
  expected: number
  currentMonth: number
  average: number
  gap: number
  gapToMonth: number
  gapToAverage: number
  deadline: Date | null
  deadlineActive: boolean
  daysRemaining: number | null
  goalReached: boolean
  monthGoalReached: boolean
  averageGoalReached: boolean
  urgentRisk: boolean
  mode: 'unavailable' | 'active' | 'grace' | 'inactive'
}

export type ProductPricingContext = {
  isLoggedIn: boolean
  showPrices: boolean
  document?: string | null
  stateRegistration?: string | null
  tipoPessoa?: number | null
  markupPercentual?: number | null
  distributionEnabled: boolean
  distributionActive: boolean
  distributionSpecial?: boolean
  distributionStatusResolved: boolean
  distributionInfo?: PricingDistributionInfo | null
  showRealStock: boolean
  stockStatusMode: number
}

export type ResolvedProductPricing = {
  audience: PricingAudience
  canShowPrice: boolean
  publicPrice: number | null
  actualPrice: number | null
  compareAtPrice: number | null
  hasCompareAtPrice: boolean
  discountPercent: number
  installmentValue: number | null
  potentialPrice: number | null
  potentialPriceKind: PricingHintKind
  appliedPriceKind: 'public' | 'distribution'
  displayDistributionBenefit: boolean
}

export const shouldPreviewDistributionBenefitPrice = (
  pricing: Pick<ResolvedProductPricing, 'actualPrice' | 'potentialPrice' | 'potentialPriceKind'>
) => (
  pricing.potentialPriceKind === 'distribution'
  && typeof pricing.potentialPrice === 'number'
  && Number.isFinite(pricing.potentialPrice)
  && pricing.potentialPrice > 0
  && typeof pricing.actualPrice === 'number'
  && Number.isFinite(pricing.actualPrice)
  && pricing.actualPrice > 0
)

export const DEFAULT_PRODUCT_PRICING_CONTEXT: ProductPricingContext = {
  isLoggedIn: false,
  showPrices: false,
  document: null,
  stateRegistration: null,
  tipoPessoa: null,
  markupPercentual: 0,
  distributionEnabled: false,
  distributionActive: false,
  distributionSpecial: false,
  distributionStatusResolved: true,
  distributionInfo: null,
  showRealStock: false,
  stockStatusMode: 2,
}

export const normalizeDocumentDigits = (value: string | null | undefined) =>
  (value || '').replace(/\D/g, '')

export const normalizeStateRegistration = (value: string | null | undefined) =>
  (value || '').replace(/\W/g, '').toUpperCase()

export const hasValidStateRegistration = (value: string | null | undefined) => {
  const normalized = normalizeStateRegistration(value)
  return Boolean(normalized && normalized !== 'ISENTO' && normalized !== 'ISENTA')
}

const normalizePositivePrice = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null

const normalizeMarkupPercentual = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0

const applyMarkup = (value: number | null, markupPercentual: number) => {
  if (value === null || markupPercentual <= 0) return value
  return Number((value * (1 + (markupPercentual / 100))).toFixed(2))
}

export const resolvePricingAudience = (
  context: Pick<ProductPricingContext, 'isLoggedIn' | 'document' | 'stateRegistration' | 'tipoPessoa'>
): PricingAudience => {
  if (!context.isLoggedIn) return 'guest'

  if (context.tipoPessoa === 1) return 'cpf'
  if (context.tipoPessoa === 2) {
    return hasValidStateRegistration(context.stateRegistration) ? 'cnpj_with_ie' : 'cnpj_without_ie'
  }

  const digits = normalizeDocumentDigits(context.document)
  if (digits.length === 11) return 'cpf'
  if (digits.length === 14) {
    return hasValidStateRegistration(context.stateRegistration) ? 'cnpj_with_ie' : 'cnpj_without_ie'
  }

  return 'unknown'
}

const resolvePublicPrice = (product: Product) => (
  normalizePositivePrice(product.price)
  ?? normalizePositivePrice(product.discountedPrice)
  ?? normalizePositivePrice(product.originalPrice)
)

const resolvePublicCompareAtPrice = (product: Product, publicPrice: number | null) => {
  const listPrice = normalizePositivePrice(product.originalPrice)
  if (publicPrice === null || listPrice === null || listPrice <= publicPrice) return null
  return listPrice
}

const buildDiscountPercent = (compareAtPrice: number | null, actualPrice: number | null) => {
  if (compareAtPrice === null || actualPrice === null || compareAtPrice <= actualPrice) return 0
  return Math.max(1, Math.round(((compareAtPrice - actualPrice) / compareAtPrice) * 100))
}

const resolveBaseProductPricing = (
  product: Product,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
): ResolvedProductPricing => {
  const audience = resolvePricingAudience(context)
  const publicPrice = resolvePublicPrice(product)
  const publicCompareAtPrice = resolvePublicCompareAtPrice(product, publicPrice)
  const distributionPrice = normalizePositivePrice(product.distributionPrice)
  const canApplyDistributionPrice = distributionPrice !== null && (
    publicPrice === null || distributionPrice < publicPrice
  )
  let actualPrice = publicPrice
  let compareAtPrice = publicCompareAtPrice
  let appliedPriceKind: ResolvedProductPricing['appliedPriceKind'] = 'public'
  let installmentValue = publicPrice !== null
    ? (normalizePositivePrice(product.installmentValue) ?? publicPrice / 12)
    : null

  if (
    context.showPrices
    && context.isLoggedIn
    && context.distributionEnabled
    && context.distributionActive
    && canApplyDistributionPrice
  ) {
    actualPrice = distributionPrice
    compareAtPrice = publicPrice !== null && publicPrice > distributionPrice ? publicPrice : null
    installmentValue = distributionPrice / 12
    appliedPriceKind = 'distribution'
  }

  let potentialPrice: number | null = null
  let potentialPriceKind: PricingHintKind = null

  if (context.showPrices && context.isLoggedIn) {
    if (
      context.distributionEnabled
      && context.distributionStatusResolved
      && !context.distributionActive
      && !context.distributionSpecial
      && canApplyDistributionPrice
      && publicPrice !== null
    ) {
      potentialPrice = distributionPrice
      potentialPriceKind = 'distribution'
    }
  }

  const canShowPrice = context.showPrices && actualPrice !== null
  const displayDistributionBenefit = appliedPriceKind === 'distribution' && !context.distributionSpecial
  const visibleCompareAtPrice = appliedPriceKind === 'distribution' && context.distributionSpecial
    ? null
    : compareAtPrice
  const hasCompareAtPrice = visibleCompareAtPrice !== null
    && actualPrice !== null
    && visibleCompareAtPrice > actualPrice

  return {
    audience,
    canShowPrice,
    publicPrice,
    actualPrice,
    compareAtPrice: hasCompareAtPrice ? visibleCompareAtPrice : null,
    hasCompareAtPrice,
    discountPercent: buildDiscountPercent(hasCompareAtPrice ? visibleCompareAtPrice : null, actualPrice),
    installmentValue: canShowPrice ? installmentValue : null,
    potentialPrice,
    potentialPriceKind,
    appliedPriceKind,
    displayDistributionBenefit,
  }
}

export const resolveProductPricing = (
  product: Product,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
): ResolvedProductPricing => {
  if (getBuyerCommerceContext().international) {
    const actualPrice = normalizePositivePrice(product.price)
    return {
      audience: 'unknown',
      canShowPrice: context.showPrices && actualPrice !== null,
      publicPrice: actualPrice,
      actualPrice,
      compareAtPrice: null,
      hasCompareAtPrice: false,
      discountPercent: 0,
      installmentValue: null,
      potentialPrice: null,
      potentialPriceKind: null,
      appliedPriceKind: 'public',
      displayDistributionBenefit: false,
    }
  }

  const basePricing = resolveBaseProductPricing(product, context)
  const markupPercentual = normalizeMarkupPercentual(context.markupPercentual ?? null)
  if (markupPercentual <= 0 || !context.isLoggedIn) {
    return basePricing
  }

  const actualPrice = applyMarkup(basePricing.actualPrice, markupPercentual)
  const compareAtPrice = applyMarkup(basePricing.compareAtPrice, markupPercentual)
  const potentialPrice = applyMarkup(basePricing.potentialPrice, markupPercentual)
  const installmentValue = applyMarkup(basePricing.installmentValue, markupPercentual)
  const hasCompareAtPrice = compareAtPrice !== null && actualPrice !== null && compareAtPrice > actualPrice

  return {
    ...basePricing,
    publicPrice: applyMarkup(basePricing.publicPrice, markupPercentual),
    actualPrice,
    compareAtPrice: hasCompareAtPrice ? compareAtPrice : null,
    hasCompareAtPrice,
    discountPercent: buildDiscountPercent(hasCompareAtPrice ? compareAtPrice : null, actualPrice),
    installmentValue: installmentValue ?? null,
    potentialPrice,
  }
}

export const isProductPendingTechnicalPrice = (
  product: Pick<Product, 'pricingPending'>,
  value: number | null | undefined
) => (
  product.pricingPending === true
  && typeof value === 'number'
  && Number.isFinite(value)
  && value > 0
  && value <= 0.01
)

/**
 * `pricingPending` sinaliza que ao menos uma modalidade de preço do produto
 * ainda está aquecendo no backend. Isso não deve esconder o preço normal que
 * já chegou quando a modalidade restante é apenas uma prévia (por exemplo,
 * o benefício de distribuição de um comprador que ainda não está ativo).
 *
 * Para distribuição ativa, porém, o preço de distribuição é o valor
 * aplicável à compra e continua sendo obrigatório antes de liberar o card.
 */
export const isProductDisplayedPricePending = (
  product: Pick<Product, 'pricingPending' | 'distributionPrice'>,
  context: Pick<
    ProductPricingContext,
    'isLoggedIn' | 'showPrices' | 'distributionEnabled' | 'distributionActive'
  >,
  pricing: Pick<ResolvedProductPricing, 'actualPrice' | 'canShowPrice'>
) => {
  if (product.pricingPending !== true || !context.showPrices) return false
  const displayedPrice = normalizePositivePrice(pricing.actualPrice)
  if (
    !pricing.canShowPrice
    || displayedPrice === null
    || isProductPendingTechnicalPrice(product, displayedPrice)
  ) return true

  return Boolean(
    context.isLoggedIn
    && context.distributionEnabled
    && context.distributionActive
    && normalizePositivePrice(product.distributionPrice) === null
  )
}

export type ProductPricingCandidate = {
  product: Product
  pricing: ResolvedProductPricing
}

/**
 * Resolve o menor preço efetivamente exibível entre variações.
 *
 * O catálogo pode entregar R$ 0,01 temporariamente enquanto o preço de uma
 * variação ainda está sendo calculado. Esse marcador nunca pode vencer o
 * preço real das demais variações no "A partir de" do detalhe.
 */
export const resolveLowestVisibleProductPricing = (
  products: Product[],
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT,
): ProductPricingCandidate | null => {
  let lowest: ProductPricingCandidate | null = null

  for (const product of products) {
    const pricing = resolveProductPricing(product, context)
    const actualPrice = pricing.actualPrice
    if (
      !pricing.canShowPrice
      || typeof actualPrice !== 'number'
      || !Number.isFinite(actualPrice)
      || actualPrice <= 0
      || isProductDisplayedPricePending(product, context, pricing)
    ) {
      continue
    }

    if (lowest === null || actualPrice < (lowest.pricing.actualPrice ?? Number.POSITIVE_INFINITY)) {
      lowest = { product, pricing }
    }
  }

  return lowest
}

/**
 * Variações sem preço calculado não podem ser selecionadas por compradores
 * autenticados. Antes do login (ou enquanto o contexto não expõe preços), a
 * variação continua visível para preservar o catálogo fechado da loja.
 */
export const shouldShowProductVariation = (
  product: Product,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
) => {
  if (!context.isLoggedIn || !context.showPrices) return true

  const price = resolveProductPricing(product, context).actualPrice
  return typeof price === 'number' && Number.isFinite(price) && price > 0
}

export const resolveCartItemUnitPrice = (
  product: Product,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
) => resolveProductPricing(product, context).actualPrice ?? 0

export const resolveCartItemBaseUnitPrice = (
  product: Product,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
) => resolveBaseProductPricing(product, context).actualPrice ?? 0

export const hasLockedCartItemPricing = (
  item: Pick<CartItem, 'lockedUnitPrice' | 'lockedSubtotal' | 'lockedTotal' | 'checkoutSource'>
) => (
  item.checkoutSource === 'quote'
  && (
    typeof item.lockedUnitPrice === 'number'
    || typeof item.lockedSubtotal === 'number'
    || typeof item.lockedTotal === 'number'
  )
)

export const resolveLockedCartItemUnitPrice = (item: Pick<CartItem, 'quantity' | 'lockedUnitPrice' | 'lockedSubtotal'>) => {
  if (typeof item.lockedUnitPrice === 'number' && Number.isFinite(item.lockedUnitPrice) && item.lockedUnitPrice >= 0) {
    return item.lockedUnitPrice
  }

  if (
    typeof item.lockedSubtotal === 'number'
    && Number.isFinite(item.lockedSubtotal)
    && item.lockedSubtotal >= 0
    && typeof item.quantity === 'number'
    && Number.isFinite(item.quantity)
    && item.quantity > 0
  ) {
    return item.lockedSubtotal / item.quantity
  }

  return 0
}

export const resolveEffectiveCartItemUnitPrice = (
  item: CartItem,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
) => (
  hasLockedCartItemPricing(item)
    ? resolveLockedCartItemUnitPrice(item)
    : resolveCartItemUnitPrice(item.product, context)
)

export const resolveEffectiveCartItemSubtotal = (
  item: CartItem,
  context: ProductPricingContext = DEFAULT_PRODUCT_PRICING_CONTEXT
) => {
  if (hasLockedCartItemPricing(item) && typeof item.lockedSubtotal === 'number' && Number.isFinite(item.lockedSubtotal)) {
    return Math.max(0, item.lockedSubtotal)
  }

  return Math.max(0, resolveEffectiveCartItemUnitPrice(item, context) * item.quantity)
}
